import {Box, Button, Dialog, DialogActions, DialogContent, useTheme} from "@mui/material";
import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import BootstrapDialogTitle from "../../../common/components/BootstrapDialogTitle";
import CustomStepper from "../../../common/components/CustomStepper";
import {BASE_DEFINITION_FORM_STEPS} from "../../../constants";
import {acrissListAtom, greenDiscountCreationAtom, initialGreenDiscountCreation} from "../../../state/greenDiscount";
import GreenDiscountDefinitionForm from "./GreenDiscountDefinitionForm";
import GreenDiscountMatrix from "./GreenDiscountMatrix";
import {useGreenDiscountDetailRepository} from "../../../api/repositories/GreenDiscountDetailRepository";
import {SelectElementModelBE} from "../../../api/types/generic";
import {FormGreenDiscountModel} from "../../../api/types/greenDiscount";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";

type GreenDiscountCreationFormProps = {
    isOpen: boolean;
    onDone: () => void;
    onClose: () => void;
    onStepChange?: (index: number) => void;
    refresh: Function;
};

const UpsellingRateFormContainer = ({
                                        isOpen,
                                        onClose,
                                        onStepChange,
                                        onDone,
                                        refresh
                                    }: GreenDiscountCreationFormProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // use Theme
    const theme = useTheme();

    // Use Recoil
    const [greenDiscountCreation, setGreenDiscountCreation] = useRecoilState(greenDiscountCreationAtom);
    const [acrissList, setAcrissList] = useRecoilState(acrissListAtom)
    const {readOnly, completed, step, definitionData} = greenDiscountCreation;

    // Use State
    const [initialValues] = useState<FormGreenDiscountModel>({
        name: "",
        description: "",
        commercialSegment: null,
        commercialCategory: null,
        reservationFrom: "",
        reservationTo: "",
        pickupFrom: "",
        pickupTo: "",
        dealerId: null,
        legalEntity: null,
        dealer: null
    });

    const submitRef = useRef<HTMLButtonElement>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Repository Hooks
    const {getAcrissFuelType} = useGreenDiscountDetailRepository();

    const getStaticContent = async () => {
        const list: SelectElementModelBE[] = await getAcrissFuelType()
        setAcrissList(list)
    }

    useEffect(() => {
        getStaticContent()
    }, [])

    const validationSchema = (t: Function, definitionData: FormGreenDiscountModel | undefined) => {
        const yupReservationFrom = yupReservationFromFunc({
            t,
            id: definitionData?.id || null,
            reservationFrom: definitionData?.reservationFrom as string
        });
        const yupReservationTo = yupReservationToFunc({
            t,
            id: definitionData?.id || null,
            reservationTo: definitionData?.reservationTo as string,
            reservationFromFieldName: "reservationFrom"
        });
        const yupPickupFrom = yupPickupFromFunc({
            t,
            id: definitionData?.id || null,
            pickupFrom: definitionData?.pickupFrom as string
        });
        const yupPickupTo = yupPickupToFunc({
            t,
            id: definitionData?.id || null,
            reservationTo: definitionData?.reservationTo as string,
            pickupFrom: definitionData?.pickupFrom as string,
            pickupTo: definitionData?.pickupTo as string,
            pickupFromFieldName: "pickupFrom"
        });
        return yup
            .object()
            .shape({
                name: yup.string().required(t("translation:validation.fieldMandatory")).min(2),
                commercialCategory: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .required(t("translation:validation.fieldMandatory"))
                    .nullable(true),
                commercialSegment: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .required(t("translation:validation.fieldMandatory"))
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                description: yup.string().nullable(),
                reservationFrom: yupReservationFrom,
                reservationTo: yupReservationTo,
                pickupFrom: yupPickupFrom,
                pickupTo: yupPickupTo
            })
            .defined();
    }

    // Use Form Context
    const form = useForm<FormGreenDiscountModel>({
        defaultValues: definitionData || initialValues,
        resolver: yupResolver(
            validationSchema(t, definitionData),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: false
    });

    const currentStep = () => {
        switch (step) {
            case 0:
                return <GreenDiscountDefinitionForm submitRef={submitRef} refresh={refresh}/>;
            case 1:
                return <GreenDiscountMatrix submitRef={submitRef}/>;
            case 2:
                return <GreenDiscountMatrix submitRef={submitRef}/>;
            case BASE_DEFINITION_FORM_STEPS.DONE:
                onDone();
                setGreenDiscountCreation(initialGreenDiscountCreation);
                return;
            default:
                return null;
        }
    };

    const onSubmit = () => {
        submitRef?.current?.click();
    };

    const handleOnClose = () => {
        onClose();
        form.reset(initialValues)
    };

    const saveAndQuit = async () => {
        const name = await form.trigger("name");
        const description = await form.trigger("description");
        const commercialSegment = await form.trigger("commercialSegment");
        const commercialCategory = await form.trigger("commercialCategory");
        const reservationFrom = await form.trigger("reservationFrom");
        const reservationTo = await form.trigger("reservationTo");
        const pickupFrom = await form.trigger("pickupFrom");
        const pickupTo = await form.trigger("pickupTo");
        const dealerId = await form.trigger("dealerId");
        if (name && description && commercialSegment && commercialCategory && reservationFrom && reservationTo && pickupFrom && pickupTo && dealerId) {
            onSubmit();
            handleOnClose();
        }
    };

    const handleChangeStep = (clickedStep: number) => {
        if (clickedStep <= step || completed.slice(step, clickedStep).every((value) => value === true)) {
            onStepChange && onStepChange(clickedStep);
            setGreenDiscountCreation({...greenDiscountCreation, step: clickedStep});
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:steps.warningNextStep"),
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    return (
        <Dialog open={isOpen} maxWidth={"xl"} fullWidth>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleOnClose}>
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                    <CustomStepper
                        activeStep={step}
                        completed={completed}
                        steps={[
                            t("pricing:steps.definition"),
                            t("pricing:steps.matrix")
                        ]}
                        handleStep={handleChangeStep}
                    />
                </Box>
            </BootstrapDialogTitle>
            <DialogContent style={{color: theme.palette.primary.main}}>
                <FormProvider {...form}>
                    {currentStep()}
                </FormProvider>
            </DialogContent>
            {!readOnly ? (
                <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                    <Button variant="outlined" color={"accent"} onClick={handleOnClose}>
                        {t("pricing:actions.cancel")}
                    </Button>
                    {step === 1 || (
                        <Button variant="outlined" color={"accent"} onClick={saveAndQuit}>
                            {t("pricing:actions.saveAndExit")}
                        </Button>
                    )}
                    <Button
                        sx={{marginLeft: "16px"}}
                        color={"accent"}
                        onClick={onSubmit}
                        onSubmit={onSubmit}
                    >
                        {step === 1
                            ? t("pricing:actions.saveAndExit")
                            : t("pricing:actions.saveAndProceed")}
                    </Button>
                </DialogActions>
            ) : (
                <div style={{padding: "1rem"}}/>
            )}
        </Dialog>
    );
};

export default UpsellingRateFormContainer;
